import * as layout from '../../../components/markLayout.module.css';
import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import {
    MarkButton,
    MarkInput,
    MarkSelect,
    MarkHeading,
    MarkLabel
} from '../../../components/MarkControls';
import React, { useId, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../../components/InlineLoader';
import useDemoProfiles from '../../../api/useDemoProfiles';
import useContact from '../../../api/useContact';
import useTestEnrollDrip from '../../../api/useTestEnrollDrip';
import { extractDripErrorCode, extractDripErrorMessage } from '../dripFormUtils';
import {
    buildEnrollBody,
    evaluateEnrollGate,
    filterDemoProfilesPage,
    filterMembershipsByTargetKind,
    filterProspectDetailsByType
} from './demoTestLogic';
import type { AxiosError } from 'axios';
import type { DemoDripInstance, DemoProfile, DripDetail } from '../../../types';
const LIMIT = 50;
type Props = {
    drip: DripDetail;
    onClose: () => void;
    onEnrolled: (instance: DemoDripInstance) => void;
    // Profile ids with a Reset currently in flight. Reset deletes every
    // instance for a profile server-side; enrolling that same profile while
    // it's pending could land a brand-new instance that the reset's later
    // local-state update would then wrongly mark reset too.
    resetPendingProfileIds: Set<number>;
    fastForwardPendingProfileIds: Set<number>;
    onResetProfile: (profileId: number) => Promise<boolean>;
};

const DemoProfilePicker: React.FC<Props> = ({
    drip,
    onClose,
    onEnrolled,
    resetPendingProfileIds,
    fastForwardPendingProfileIds,
    onResetProfile
}) => {
    const [offset, setOffset] = useState(0);
    const [resetInProgress, setResetInProgress] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedProfile, setSelectedProfile] = useState<DemoProfile | null>(null);
    const [membershipId, setMembershipId] = useState<number | null>(null);
    const [prospectDetailsId, setProspectDetailsId] = useState<number | null>(null);
    const headingId = useId();
    // Re-focus the heading on every step change, not just on mount — list
    // <-> confirmation swaps out the previously focused element (a profile
    // row, or Back), and without this a keyboard/screen-reader user's focus
    // falls back to the document body instead of following into the next
    // step's content.
    const enroll = useTestEnrollDrip();
    const profilesQuery = useDemoProfiles({ offset, limit: LIMIT });
    const totalCount = profilesQuery.data?.data.total_count ?? 0;
    const allProfiles = profilesQuery.data?.data.contacts ?? [];
    const visibleProfiles = filterDemoProfilesPage(allProfiles, query);
    const canPrev = offset > 0;
    const canNext = offset + LIMIT < totalCount;
    // Selecting a profile advances to step 2 and starts that profile's tuple
    // fresh — never carry a membership/prospect selection from a prior profile.
    // Also reset the enroll mutation itself: it isn't keyed by profile, so a
    // prior profile's failure would otherwise stay in isError/error and show
    // under this new profile's confirmation step before any request runs.
    const selectProfile = (profile: DemoProfile) => {
        setSelectedProfile(profile);
        setMembershipId(null);
        setProspectDetailsId(null);
        enroll.reset();
        requestAnimationFrame(() => document.getElementById(headingId)?.focus());
    };
    const backToList = () => {
        requestAnimationFrame(() => document.getElementById(headingId)?.focus());
        setSelectedProfile(null);
        setMembershipId(null);
        setProspectDetailsId(null);
    };
    const contactQuery = useContact(selectedProfile?.profile_id ?? 0, selectedProfile != null);
    // Fail closed while a background refresh is running — a cached success
    // can be showing pre-refresh membership/prospect data even though
    // isSuccess stays true during that refetch window.
    const contact = contactQuery.isFetching ? undefined : contactQuery.data?.data;
    const eligibleMemberships = filterMembershipsByTargetKind(
        contact?.memberships ?? [],
        drip.target_kind
    );
    const resetPendingForProfile =
        selectedProfile != null && resetPendingProfileIds.has(selectedProfile.profile_id);

    const duplicateEnrollment = extractDripErrorCode(enroll.error) === 'instance_already_enrolled';
    const resetUnavailable =
        enroll.isPending ||
        resetInProgress ||
        resetPendingForProfile ||
        (selectedProfile != null && fastForwardPendingProfileIds.has(selectedProfile.profile_id));
    const resetSelectedProfile = async () => {
        if (!selectedProfile || resetUnavailable) return;
        setResetInProgress(true);
        try {
            if (await onResetProfile(selectedProfile.profile_id)) {
                enroll.reset();
                await contactQuery.refetch();
            }
        } finally {
            setResetInProgress(false);
        }
    };

    const baseGate = evaluateEnrollGate(drip, selectedProfile?.profile_id ?? null, contact, {
        membershipId,
        prospectDetailsId
    });
    const profileBusy =
        resetInProgress ||
        resetPendingForProfile ||
        (selectedProfile != null && fastForwardPendingProfileIds.has(selectedProfile.profile_id));
    const gate = profileBusy
        ? {
              allowed: false,
              reason: 'A demo action is in progress for this profile — wait for it to finish before enrolling again.'
          }
        : baseGate;
    const prospectOptions =
        contact != null
            ? filterProspectDetailsByType(
                  contact.prospect_details,
                  drip.trigger_type === 'PROSPECT' ? drip.prospect_type : null
              )
            : [];
    const handleEnroll = () => {
        if (!gate.allowed || selectedProfile == null || enroll.isPending) return;
        const body = buildEnrollBody(drip, selectedProfile.profile_id, {
            membershipId,
            prospectDetailsId
        });
        enroll.mutate(body, {
            onSuccess: (response) => {
                onEnrolled({
                    drip_instance_id: response.data.drip_instance_id,
                    status: response.data.status,
                    profile_id: selectedProfile.profile_id,
                    enrolled_at: Date.now(),
                    current_step: null,
                    due_at: null,
                    reset: false
                });
            }
        });
    };
    return (
        <MarkModal
            onClose={onClose}
            isPending={enroll.isPending || resetInProgress}
            aria-labelledby={headingId}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding>
                <MarkHeading
                    role="h4"
                    id={headingId}
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    {selectedProfile == null ? 'Pick a demo profile' : 'Confirm demo profile'}
                </MarkHeading>
                {selectedProfile == null ? (
                    <>
                        <MarkInput
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.currentTarget.value)}
                            placeholder="Filter this page by email or name"
                            aria-label="Filter demo profiles on this page"
                            style={{
                                marginBottom: '1rem'
                            }}
                        />
                        {profilesQuery.isLoading ? (
                            <InlineLoader />
                        ) : profilesQuery.isError ? (
                            <div role="alert" className={layout.notice}>
                                <P
                                    noDefaultMargin
                                    size="small"
                                    styles={{
                                        color: 'var(--mark-danger)'
                                    }}
                                >
                                    {extractDripErrorMessage(profilesQuery.error as AxiosError)}
                                </P>
                                <MarkButton
                                    type="button"
                                    onClick={() => profilesQuery.refetch()}
                                    style={{
                                        marginTop: '0.6rem'
                                    }}
                                >
                                    Retry
                                </MarkButton>
                            </div>
                        ) : totalCount === 0 ? (
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                No demo profiles configured. Ask platform to seed one.
                            </P>
                        ) : visibleProfiles.length === 0 ? (
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                No profiles on this page match &ldquo;{query}&rdquo;.
                            </P>
                        ) : (
                            <ul
                                style={{
                                    listStyle: 'none',
                                    margin: 0,
                                    padding: 0
                                }}
                            >
                                {visibleProfiles.map((profile) => (
                                    <li key={profile.profile_id}>
                                        <MarkButton
                                            type="button"
                                            onClick={() => selectProfile(profile)}
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                                textAlign: 'left',
                                                marginBottom: '0.4rem'
                                            }}
                                        >
                                            {profile.first_name} {profile.last_name} —{' '}
                                            {profile.email}
                                        </MarkButton>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '1.2rem'
                            }}
                        >
                            <div className={layout.actions}>
                                <MarkButton
                                    type="button"
                                    onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                                    disabled={!canPrev}
                                    style={{
                                        display: 'block',
                                        textAlign: 'left',
                                        width: 'auto',
                                        marginBottom: 0
                                    }}
                                >
                                    Previous
                                </MarkButton>
                                <MarkButton
                                    variant="primary"
                                    type="button"
                                    onClick={() => setOffset((o) => o + LIMIT)}
                                    disabled={!canNext}
                                    style={{
                                        display: 'block',
                                        textAlign: 'left',
                                        width: 'auto',
                                        marginBottom: 0
                                    }}
                                >
                                    Next
                                </MarkButton>
                            </div>
                            <MarkButton
                                type="button"
                                onClick={onClose}
                                style={{
                                    display: 'block',
                                    textAlign: 'left',
                                    width: 'auto',
                                    marginBottom: 0
                                }}
                            >
                                Cancel
                            </MarkButton>
                        </div>
                    </>
                ) : (
                    <>
                        <P
                            noDefaultMargin
                            styles={{
                                marginBottom: '0.8rem'
                            }}
                        >
                            {selectedProfile.first_name} {selectedProfile.last_name} —{' '}
                            {selectedProfile.email}
                        </P>
                        {contactQuery.isLoading ? (
                            <InlineLoader />
                        ) : contactQuery.isError ? (
                            <div role="alert" className={layout.notice}>
                                <P
                                    noDefaultMargin
                                    size="small"
                                    styles={{
                                        color: 'var(--mark-danger)'
                                    }}
                                >
                                    Could not load profile details.
                                </P>
                                <MarkButton
                                    type="button"
                                    onClick={() => contactQuery.refetch()}
                                    style={{
                                        marginTop: '0.6rem'
                                    }}
                                >
                                    Retry
                                </MarkButton>
                            </div>
                        ) : (
                            <>
                                {(drip.target_kind === 'MEMBERSHIP' ||
                                    drip.target_kind === 'MEMBERSHIP_ADMIN') && (
                                    <div className={layout.field}>
                                        <MarkLabel
                                            htmlFor="demo-membership-select"
                                            style={{
                                                display: 'block',
                                                marginBottom: '0.2rem'
                                            }}
                                        >
                                            Membership
                                        </MarkLabel>
                                        <MarkSelect
                                            id="demo-membership-select"
                                            value={membershipId ?? ''}
                                            onChange={(e) =>
                                                setMembershipId(
                                                    e.currentTarget.value
                                                        ? Number(e.currentTarget.value)
                                                        : null
                                                )
                                            }
                                        >
                                            <option value="">Select a membership…</option>
                                            {eligibleMemberships.map((m) => (
                                                <option
                                                    key={m.membership_id}
                                                    value={m.membership_id}
                                                >
                                                    {m.organization_name ?? m.membership_type} (
                                                    {m.status})
                                                </option>
                                            ))}
                                        </MarkSelect>
                                    </div>
                                )}
                                {drip.target_kind === 'PROSPECT' && (
                                    <div
                                        style={{
                                            marginBottom: '1rem'
                                        }}
                                    >
                                        <MarkLabel
                                            htmlFor="demo-prospect-select"
                                            style={{
                                                display: 'block',
                                                marginBottom: '0.2rem'
                                            }}
                                        >
                                            Prospect record
                                        </MarkLabel>
                                        {prospectOptions.length === 0 ? (
                                            <P
                                                noDefaultMargin
                                                size="small"
                                                styles={{
                                                    color: 'var(--mark-warning)'
                                                }}
                                            >
                                                No {drip.prospect_type ?? 'matching'} prospect on
                                                this profile.
                                            </P>
                                        ) : (
                                            <MarkSelect
                                                id="demo-prospect-select"
                                                value={prospectDetailsId ?? ''}
                                                onChange={(e) =>
                                                    setProspectDetailsId(
                                                        e.currentTarget.value
                                                            ? Number(e.currentTarget.value)
                                                            : null
                                                    )
                                                }
                                            >
                                                <option value="">Select a prospect record…</option>
                                                {prospectOptions.map((p) => (
                                                    <option
                                                        key={p.prospect_details_id}
                                                        value={p.prospect_details_id}
                                                    >
                                                        {p.prospect_type} — {p.state}
                                                    </option>
                                                ))}
                                            </MarkSelect>
                                        )}
                                    </div>
                                )}
                                {!gate.allowed && gate.reason && (
                                    <P
                                        noDefaultMargin
                                        size="small"
                                        styles={{
                                            color: 'var(--mark-warning)',
                                            marginBottom: '0.8rem'
                                        }}
                                    >
                                        {gate.reason}
                                    </P>
                                )}
                                {enroll.isError && (
                                    <div
                                        role="alert"
                                        style={{
                                            marginBottom: '0.8rem'
                                        }}
                                        className={layout.notice}
                                    >
                                        <P
                                            noDefaultMargin
                                            size="small"
                                            styles={{
                                                color: 'var(--mark-danger)'
                                            }}
                                        >
                                            {extractDripErrorMessage(enroll.error as AxiosError)}
                                        </P>
                                        {duplicateEnrollment && (
                                            <div>
                                                <P noDefaultMargin size="small">
                                                    Reset deletes this profile's demo instances in
                                                    this drip. Then you can enroll again.
                                                </P>
                                                <button
                                                    type="button"
                                                    disabled={resetUnavailable}
                                                    onClick={() => void resetSelectedProfile()}
                                                >
                                                    {resetPendingForProfile
                                                        ? 'Resetting…'
                                                        : 'Reset demo profile'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                        <div
                            style={{
                                marginTop: '1.6rem'
                            }}
                            className={layout.actions}
                        >
                            <MarkButton
                                type="button"
                                onClick={backToList}
                                disabled={enroll.isPending || resetInProgress}
                                style={{
                                    display: 'block',
                                    textAlign: 'left',
                                    width: 'auto',
                                    marginBottom: 0
                                }}
                            >
                                Back
                            </MarkButton>
                            <MarkButton
                                type="button"
                                onClick={onClose}
                                disabled={enroll.isPending || resetInProgress}
                                style={{
                                    display: 'block',
                                    textAlign: 'left',
                                    width: 'auto',
                                    marginBottom: 0
                                }}
                            >
                                Cancel
                            </MarkButton>
                            <MarkButton
                                type="button"
                                onClick={handleEnroll}
                                disabled={!gate.allowed || enroll.isPending}
                                title={!gate.allowed && gate.reason ? gate.reason : undefined}
                                variant="primary"
                            >
                                {enroll.isPending ? 'Enrolling…' : 'Enroll demo profile'}
                            </MarkButton>
                        </div>
                    </>
                )}
            </Section>
        </MarkModal>
    );
};
export default DemoProfilePicker;
